import { ChangeDetectionStrategy, Component, effect, inject, untracked } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { AuthService } from '@auth0/auth0-angular'
import { StageService, trackResourceFailure } from '@saniup-webapps/shared'
import { ErrorStateComponent } from '@saniup-webapps/shared/components'
import { DashboardSettingsSyncService } from '../services/dashboard-settings-sync.service'
import { EmployeeService } from '../services/employee.service'
import { ExternalPartnerNavigationComponent } from './external-partner-navigation/external-partner-navigation.component'
import { LeistungserbringerNavigationComponent } from './leistungserbringer-navigation/leistungserbringer-navigation.component'
import { SupportCardComponent } from '../components/support-card/support-card.component'
import { willkommenZeigen } from './willkommen-dialog/willkommen.util'
import { ZugangAusstehendComponent } from './zugang-ausstehend/zugang-ausstehend.component'

@Component({
  selector: 'app-navigation',
  imports: [
    LeistungserbringerNavigationComponent,
    ExternalPartnerNavigationComponent,
    MatButtonModule,
    MatProgressBarModule,
    ErrorStateComponent,
    ZugangAusstehendComponent,
    SupportCardComponent,
  ],
  templateUrl: './navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './navigation.component.scss',
})
export class NavigationComponent {
  private readonly authService = inject(AuthService)
  readonly employeeService = inject(EmployeeService)

  /**
   * Diese eine Abfrage traegt die ganze Anwendung: aus ihr folgt, welche Seitenleiste gilt.
   * Sie stand bis 2026-08-10 in einem nackten `lib-resource-loading` — schlug sie fehl, war das
   * Portal auf eine Textzeile ohne Logo, ohne Abmelden und ohne Wiederholen reduziert.
   * `trackResourceFailure` bringt Ursache, Wiederholen und die Selbstheilung mit.
   */
  readonly bootFailure = trackResourceFailure(() => [this.employeeService.currentlyLoggedInEmployee])

  constructor() {
    // Hier und nicht in app.config: diese Komponente ist die erste hinter dem Auth-Guard, der
    // Aufruf braucht also keinen eigenen Login-Check. Weiter oben liefe er gegen einen 401.
    inject(DashboardSettingsSyncService).start()

    // Willkommensdialog, einmal je Konto (Regel in `willkommen.util.ts`). Hier, weil diese Huelle vor
    // beiden Seitenleisten steht: auch externe Partner stehen mit Namen in den Aufgaben.
    const dialog = inject(MatDialog)
    const stage = inject(StageService)
    let gezeigt = false
    effect(() => {
      const geladen = this.employeeService.currentlyLoggedInEmployee
      if (gezeigt || !geladen.hasValue()) return
      const ich = geladen.value()
      if (!ich || !willkommenZeigen(ich, this.employeeService.isSaniupEmployee(), stage.isProd())) return
      gezeigt = true
      // Nachgeladen: diese Huelle liegt im Initial-Bundle, der Dialog samt Zuschnitt und Formular
      // kaeme sonst bei jedem Seitenaufruf mit, obwohl er je Konto einmal erscheint (Budget 1,75 MB).
      void import('./willkommen-dialog/willkommen-dialog.component').then(({ WillkommenDialogComponent }) =>
        untracked(() =>
          dialog.open(WillkommenDialogComponent, {
            data: { ich },
            width: '32rem',
            maxWidth: '95vw',
            disableClose: true,
            ariaLabelledBy: 'willkommen-titel',
          }),
        ),
      )
    })
  }

  logout() {
    this.authService.logout({ logoutParams: { returnTo: window.location.origin } })
  }
}
